/**
 * /login — enterprise-grade two-column sign-in for DDARP.
 *
 * Left column (lg+):  DopplerPulseMap animated SVG + trust line
 * Right column:       DDARP wordmark + sign-in form + footer
 *
 * Mobile (< lg):       single-column form-only layout. The pulse map is
 *                      hidden to keep the form above the fold.
 *
 * The auth flow logic is unchanged from the V1 design; only the framing
 * was redesigned per Public Pages Refinements Phase 10 (2026-05-26).
 */
import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Input, GradientText } from '@/components/common';
import { Lock, Mail, AlertCircle, ArrowLeft } from 'lucide-react';
import { landingFor, prefixFor } from '@/lib/rolePath';
import DopplerPulseMap from '@/components/marketing/DopplerPulseMap';


export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // The page they tried to reach before being bounced to /login (if any).
  // We honor it only when it belongs to the logged-in user's prefix; else
  // we send them to their own landing.
  const fromPath = (location.state as { from?: { pathname: string } })?.from?.pathname || null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setIsSubmitting(true);

    try {
      const user = await login(email.trim().toLowerCase(), password, rememberMe);
      const myPrefix = prefixFor(user.role);
      const target =
        fromPath && fromPath.startsWith(`/${myPrefix}/`)
          ? fromPath
          : landingFor(user.role);
      navigate(target, { replace: true });
    } catch (err) {
      setLoginError(err instanceof Error ? err.message : 'invalid email or password');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* ── Left column (lg+ only) — Doppler pulse map ──────────────── */}
      <div className="hidden lg:block relative">
        <DopplerPulseMap />
      </div>

      {/* ── Right column — form ─────────────────────────────────────── */}
      <div className="relative flex flex-col min-h-screen">
        {/* Back to Home (top-left) */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="absolute top-6 left-6 z-20"
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 text-sm transition-colors"
          >
            <ArrowLeft size={16} />
            <span>Back to Home</span>
          </Link>
        </motion.div>

        {/* Center the form vertically within the column */}
        <div className="flex-1 flex items-center justify-center px-6 py-16 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.15 }}
            className="w-full max-w-md"
          >
            {/* Wordmark + tagline */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <img
                  src="/images/logos/ddarp-icon.svg"
                  alt=""
                  aria-hidden
                  className="w-10 h-10"
                />
                <h1 className="text-4xl font-bold leading-none">
                  <GradientText text="DDARP" />
                </h1>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-1.5">
                Sign in to continue
              </h2>
              <p className="text-sm text-gray-500">
                Welcome back. Enter your credentials to access the platform.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error */}
              {loginError && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  role="alert"
                  aria-live="polite"
                  className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700"
                >
                  <AlertCircle size={18} className="flex-shrink-0" />
                  <span className="text-sm">{loginError}</span>
                </motion.div>
              )}

              {/* Email */}
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                leftIcon={<Mail size={18} />}
                fullWidth
                disabled={isSubmitting}
                required
                autoComplete="username email"
              />

              {/* Password */}
              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                leftIcon={<Lock size={18} />}
                fullWidth
                disabled={isSubmitting}
                required
                autoComplete="current-password"
              />

              {/* Remember-me + Forgot password row */}
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isSubmitting}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="text-gray-600">Remember me for 30 days</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-primary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                isLoading={isSubmitting}
                disabled={!email || !password}
              >
                Sign in
              </Button>
            </form>

            {/* Security notice */}
            <p className="text-center text-xs text-gray-400 mt-8 leading-relaxed">
              This is a secure interface. Sign-in attempts are logged for audit.
              <br />
              By signing in you agree to MadHat Media, Inc.&apos;s acceptable
              use policy.
            </p>
          </motion.div>
        </div>

        {/* Footer pinned to bottom of right column */}
        <footer className="px-6 py-4 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            &copy; 2014-2026 MadHat Media, Inc. All rights reserved.
          </p>
        </footer>
      </div>
    </div>
  );
}
